/**
 * FacilitiesSearchList
 *
 * Isolates facility search so that:
 * - Only the list (items) re-renders when search results change.
 * - Title "Facilities" and the search input do NOT re-render when results update.
 *
 * Supports Step 1 (page-based) and Step 3 (infinite with organizationId).
 */

import React, { useEffect, memo, useMemo, useRef } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { useFacilitiesPageBased } from '../../hooks/useFacilitiesPageBased';
import { useFacilitiesInfinite } from '../../hooks/useFacilitiesInfinite';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { FacilityCard } from './FacilityCard';
import { FacilityListSkeleton } from '../organizationSkeletons/FacilityListSkeleton';

const SEARCH_DEBOUNCE_MS = 350;

/** Memoized header: title + search input. Controlled by parent so search and hook stay in sync. */
const FacilitiesSearchHeader = memo(function FacilitiesSearchHeader({ searchValue, onSearchChange }) {
  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex items-center justify-between gap-4 w-full">
        <h4
          className="font-['Roboto:Bold',sans-serif] font-bold text-[22px] text-black"
          style={{ fontVariationSettings: "'wdth' 100" }}
        >
          Facilities
        </h4>
        <div className="relative flex items-center">
          <div className="bg-white border border-[#d3d6dc] rounded-[10px] h-[37px] pl-[7px] pr-7 flex items-center gap-[10px] min-w-[154px]">
            <Search className="w-6 h-6 text-[#6e6e71] shrink-0" />
            <input
              type="text"
              placeholder="Search"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="font-['Roboto:Regular',sans-serif] font-normal text-[18px] text-[#6e6e71] bg-transparent border-none outline-none w-full placeholder:text-[#6e6e71]"
              style={{ fontVariationSettings: "'wdth' 100" }}
              aria-label="Search facilities"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * @param {Object} props
 * @param {'page-based'|'infinite'} props.variant - 'page-based' for Step 1, 'infinite' for Step 3
 * @param {string} [props.queryScope] - Isolates cache per context (e.g. 'organization-structure' vs 'cleaning-standards') so pagination in one step does not affect the other
 * @param {string} [props.organizationId] - Required when variant is 'infinite'
 * @param {string[]} props.selectedFacilityIds
 * @param {(id: string) => void} props.onSelectFacility
 * @param {() => void} props.onSelectAll
 * @param {(facilities: Array) => void} props.onFacilitiesChange
 * @param {(facility: Object) => void} [props.onEditFacility] - Step 1 only
 * @param {(facility: Object) => void} [props.onDuplicateFacility] - Step 1 only
 * @param {React.ReactNode} [props.children] - Rendered after list (e.g. Add Facility button)
 * @param {React.ReactNode} [props.emptyState] - Shown when facilities.length === 0 (e.g. empty message + Add button)
 */
export const FacilitiesSearchList = ({
  variant = 'page-based',
  queryScope,
  organizationId,
  selectedFacilityIds,
  onSelectFacility,
  onSelectAll,
  onFacilitiesChange,
  onEditFacility,
  onDuplicateFacility,
  children,
  emptyState,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const lastFacilitiesRef = useRef([]);

  const pageBased = useFacilitiesPageBased({
    queryScope,
    limit: 10,
    search: debouncedSearch ?? '',
    enabled: variant === 'page-based',
  });

  const infinite = useFacilitiesInfinite({
    organizationId: organizationId || '',
    queryScope,
    limit: 10,
    search: debouncedSearch ?? '',
    enabled: variant === 'infinite' && !!organizationId,
  });

  const isPageBased = variant === 'page-based';
  const facilities = isPageBased ? pageBased.facilities : infinite.facilities;
  const totalCount = isPageBased ? pageBased.totalCount : infinite.totalCount;
  const isLoading = isPageBased ? pageBased.isLoading : infinite.isLoading;
  const isFetching = isPageBased ? pageBased.isFetching : infinite.isFetching;
  const hasNextPage = isPageBased ? pageBased.hasNextPage : infinite.hasNextPage;
  const isFetchingNextPage = isPageBased ? pageBased.isFetchingNextPage : infinite.isFetchingNextPage;
  const fetchNextPage = isPageBased ? pageBased.fetchNextPage : infinite.fetchNextPage;

  useEffect(() => {
    if (facilities.length > 0) lastFacilitiesRef.current = facilities;
  }, [facilities]);

  useEffect(() => {
    lastFacilitiesRef.current = [];
  }, [organizationId, variant]);

  const searchTrimmed = (searchQuery && searchQuery.trim()) || '';
  const debouncedTrimmed = (debouncedSearch && String(debouncedSearch).trim()) || '';

  // Senior search UX: show "Searching..." as soon as user types; don't show list until we have results for this search
  const isDebouncing = searchTrimmed !== debouncedTrimmed;
  const isWaitingForBackend = isFetching && facilities.length === 0;
  const isSearchActive = searchTrimmed.length > 0;
  const isSearching = isSearchActive && (isDebouncing || isWaitingForBackend);

  const displayedFacilities = useMemo(() => {
    if (isSearching) return [];
    return facilities;
  }, [facilities, isSearching]);

  // Only notify parent when the list of facility ids actually changed (avoids re-render loop)
  const facilityIdsKey = useMemo(
    () => displayedFacilities.map((f) => f.id).join(','),
    [displayedFacilities],
  );
  const prevFacilityIdsRef = useRef('');
  useEffect(() => {
    if (facilityIdsKey !== prevFacilityIdsRef.current) {
      prevFacilityIdsRef.current = facilityIdsKey;
      onFacilitiesChange?.(displayedFacilities);
    }
  }, [facilityIdsKey, displayedFacilities, onFacilitiesChange]);

  const hasCachedFacilities = lastFacilitiesRef.current.length > 0;
  // Skeleton only on first load (no search). When user searches, show "Searching..." loader until results, not skeleton.
  const isInitialLoad = isLoading && facilities.length === 0 && !hasCachedFacilities && !isSearchActive;

  return (
    <div className="flex flex-col gap-5 w-full">
      {/* Title + Search */}
      <FacilitiesSearchHeader searchValue={searchQuery} onSearchChange={setSearchQuery} />

      {/* Senior search UX: as soon as user types, show Searching and hide list until backend returns */}
      {isSearching ? (
        <div
          className="flex flex-col items-center justify-center rounded-xl bg-[#f8fbfd] border border-[#e5eaf0] min-h-[200px] px-6 py-10"
          role="status"
          aria-live="polite"
          aria-label="Searching facilities"
        >
          <Loader2 className="w-8 h-8 text-[#61b8e7] animate-spin mb-4" aria-hidden />
          <p className="font-['Roboto:Regular',sans-serif] text-[15px] text-[#6e6e71] text-center">
            Searching facilities...
          </p>
          <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-[#9e9e9e] text-center mt-1">
            {isDebouncing ? 'Waiting for you to finish typing...' : 'Fetching results...'}
          </p>
        </div>
      ) : isInitialLoad ? (
        <FacilityListSkeleton />
      ) : (
      <div className="flex flex-col gap-3 w-full">
        <div className="flex items-center">
          <button
            type="button"
            onClick={onSelectAll}
            className="flex items-center gap-2.5 hover:opacity-70 transition-opacity group"
          >
            <div className="relative shrink-0">
              <input
                type="checkbox"
                checked={
                  displayedFacilities.length > 0 && selectedFacilityIds.length === displayedFacilities.length
                }
                onChange={() => {}}
                className="size-[18px] appearance-none bg-white border-2 border-[#9e9e9e] rounded-[3px] cursor-pointer checked:bg-[#61b8e7] checked:border-[#61b8e7] transition-all group-hover:border-[#61b8e7] pointer-events-none"
                aria-label="Select all facilities"
              />
              {selectedFacilityIds.length === displayedFacilities.length && displayedFacilities.length > 0 && (
                <svg
                  className="absolute left-[3px] top-[3px] w-[12px] h-[12px] pointer-events-none"
                  fill="none"
                  viewBox="0 0 12 10"
                >
                  <path
                    d="M1.5 5L4.5 8L10.5 1.5"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <span className="font-['Roboto:Medium',sans-serif] font-medium text-[14px] text-[#6e6e71] group-hover:text-[#61b8e7] transition-colors">
              Select All
            </span>
          </button>
        </div>

      {/* List + Load more + Count */}
      <div className="flex flex-col gap-3">
        {displayedFacilities.length === 0 ? (
          isSearchActive ? (
            <div className="py-8 text-center rounded-xl bg-[#f8fbfd] border border-[#e5eaf0] px-6">
              <span className="font-['Roboto:Regular',sans-serif] text-sm text-[#6e6e71]">
                No facilities match your search. Try a different term or clear the search.
              </span>
            </div>
          ) : (
            emptyState || (
              <div className="py-8 text-center">
                <span className="font-['Roboto:Regular',sans-serif] text-sm text-gray-500">
                  No facilities found
                </span>
              </div>
            )
          )
        ) : (
          <>
        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {displayedFacilities.map((facility) => (
            <FacilityCard
              key={facility.id}
              facility={facility}
              isSelected={selectedFacilityIds.includes(facility.id)}
              onSelect={onSelectFacility}
              onEdit={onEditFacility}
              onDuplicate={onDuplicateFacility}
            />
          ))}
        </div>

        {hasNextPage && (
          <div className="pt-2 flex flex-col items-center justify-center gap-3">
            {isFetchingNextPage ? (
              <div className="flex items-center gap-3 text-[#61b8e7] py-4">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="font-['Roboto:Regular',sans-serif] text-sm">
                  Loading more facilities...
                </span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fetchNextPage()}
                className="w-full px-6 py-3 border-2 border-[#61b8e7] rounded-lg text-[#61b8e7] hover:bg-[#61b8e7] hover:text-white transition-all font-['Roboto:Medium',sans-serif] text-sm shadow-sm hover:shadow-md"
              >
                Load More
              </button>
            )}
          </div>
        )}

        {displayedFacilities.length > 0 && (
          <div className="text-center py-2 border-t border-gray-100">
            <span className="font-['Roboto:Regular',sans-serif] text-xs text-gray-500">
              {`Showing ${displayedFacilities.length} of ${isSearchActive ? displayedFacilities.length : totalCount} facilities`}
            </span>
          </div>
        )}
          </>
        )}
      </div>
      </div>
      )}

      {children}
    </div>
  );
};
